import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Edit3, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Package, 
  User, 
  FileText, 
  DollarSign,
  Calendar,
  Hash,
  RefreshCw,
  Save
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getLineItemDetails, updateLineItem, validateLineItem } from '../../config/api';
import LineItemEditModal from '../../components/LineItemEditModal';

const LineItemDetails = () => {
  const { lineItemId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [showEditModal, setShowEditModal] = useState(false);

  const { data: lineItem, isLoading, error } = useQuery({
    queryKey: ['lineItemDetails', lineItemId],
    queryFn: () => getLineItemDetails(lineItemId),
    
      enabled: !!lineItemId,
      retry: 1
    
  });

  const updateMutation = useMutation({
    mutationFn: updateLineItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lineItemDetails', lineItemId] });
      toast.success('Line item updated successfully');
      setShowEditModal(false);
    },
    onError: (error) => {
      toast.error('Failed to update line item');
      console.error('Update error:', error);
    }
  });

  const validateMutation = useMutation({
    mutationFn: validateLineItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lineItemDetails', lineItemId] });
      toast.success('Line item re-validated successfully');
    },
    onError: (error) => {
      toast.error('Failed to validate line item');
      console.error('Validation error:', error);
    }
  });

  const getValidationStatusInfo = (status) => {
    const statusConfig = {
      // General statuses
      PENDING: { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, label: 'Pending' },
      VALIDATED: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Validated' },
      MANUALLY_EDITED: { color: 'bg-blue-100 text-blue-800', icon: Edit3, label: 'Manually Edited' },
      
      // Contract validation statuses
      CNTRDT: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Contract Date Mismatch' },
      CNTRNF: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Contract Not Found' },
      CNTRIN: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Contract Inactive' },
      CNTRXP: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Contract Expired' },
      CNTRNC: { color: 'bg-yellow-100 text-yellow-800', icon: FileText, label: 'Contract Not Effective' },
      CNTRCA: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Customer Not Associated' },
      CNTRNP: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Product Not in Contract' },
      
      // Product validation statuses
      PRODNF: { color: 'bg-red-100 text-red-800', icon: Package, label: 'Product Not Found' },
      PRODIN: { color: 'bg-red-100 text-red-800', icon: Package, label: 'Product Inactive' },
      PRODNDC: { color: 'bg-yellow-100 text-yellow-800', icon: Package, label: 'Invalid NDC Format' },
      PRODCS: { color: 'bg-orange-100 text-orange-800', icon: Package, label: 'Controlled Substance' },
      PRODOOS: { color: 'bg-yellow-100 text-yellow-800', icon: Package, label: 'Out of Stock' },
      
      // Customer validation statuses
      CUSTNF: { color: 'bg-red-100 text-red-800', icon: User, label: 'Customer Not Found' },
      CUSTIN: { color: 'bg-red-100 text-red-800', icon: User, label: 'Customer Inactive' },
      CUSTNC: { color: 'bg-red-100 text-red-800', icon: User, label: 'Customer Not Compliant' },
      
      // Pricing validation statuses
      PRICNF: { color: 'bg-red-100 text-red-800', icon: DollarSign, label: 'Pricing Not Found' },
      PRICVT: { color: 'bg-red-100 text-red-800', icon: DollarSign, label: 'Price Variance' },
      PRICNG: { color: 'bg-red-100 text-red-800', icon: DollarSign, label: 'Negative Pricing' },
      PRICUH: { color: 'bg-yellow-100 text-yellow-800', icon: DollarSign, label: 'Unusually High Price' },
      
      // Sales data validation statuses
      SALDNF: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Sales Data Not Found' },
      SALDPM: { color: 'bg-yellow-100 text-yellow-800', icon: FileText, label: 'Price Mismatch' },
      SALDVV: { color: 'bg-yellow-100 text-yellow-800', icon: FileText, label: 'Volume Variance' },
      
      // Business rules validation statuses
      BUSDAT: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Invalid Date' },
      BUSQTY: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Invalid Quantity' },
      BUSAMT: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Amount Mismatch' },
      BUSCOM: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Compliance Required' },
      
      // Data quality validation statuses
      DATAQTY: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Data Quality' },
      DATADUP: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Duplicate Data' },
      DATAMIS: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Missing Data' },
      
      // Legacy statuses (for backward compatibility)
      PRODUCT_NOT_FOUND: { color: 'bg-red-100 text-red-800', icon: Package, label: 'Product Not Found' },
      CUSTOMER_NOT_FOUND: { color: 'bg-red-100 text-red-800', icon: User, label: 'Customer Not Found' },
      CONTRACT_NOT_FOUND: { color: 'bg-orange-100 text-orange-800', icon: FileText, label: 'Contract Not Found' },
      PRICING_NOT_FOUND: { color: 'bg-yellow-100 text-yellow-800', icon: DollarSign, label: 'Pricing Not Found' },
      INVALID_QUANTITY: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Invalid Quantity' },
      INVALID_PRICE: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Invalid Price' },
      DUPLICATE_CLAIM: { color: 'bg-purple-100 text-purple-800', icon: AlertCircle, label: 'Duplicate Claim' },
      
      // Error statuses
      ERROR: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Error' },
      SYSTEM_ERROR: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'System Error' }
    };
    return statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, label: status };
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleSave = (updatedLineItem) => {
    updateMutation.mutate(updatedLineItem);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
  };

  const handleValidate = () => {
    validateMutation.mutate(lineItemId);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
            title="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Line Item Details</h1>
        </div>
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading line item details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
            title="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Line Item Details</h1>
        </div>
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Error Loading Line Item</h2>
          <p className="text-gray-500">Failed to load line item details. Please try again.</p>
        </div>
      </div>
    );
  }

  if (!lineItem) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
            title="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Line Item Details</h1>
        </div>
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Line Item Not Found</h2>
          <p className="text-gray-500">The requested line item could not be found.</p>
        </div>
      </div>
    );
  }

  const validationInfo = getValidationStatusInfo(lineItem.validationStatus);
  const ValidationIcon = validationInfo.icon;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
            title="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Line Item Details</h1>
            <p className="text-gray-500">Line #{lineItem.lineNo} - {lineItem.product?.name}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleValidate}
            disabled={validateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            title="Re-validate"
          >
            <RefreshCw size={16} className={validateMutation.isPending ? 'animate-spin' : ''} />
            Re-validate
          </button>
          <button
            onClick={handleEdit}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            title="Edit"
          >
            <Edit3 size={16} />
            Edit
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Product Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Package className="h-5 w-5 mr-2" />
              Product Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                <p className="text-sm text-gray-900">{lineItem.product?.name || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">NDC</label>
                <p className="text-sm text-gray-900">{lineItem.productNdc || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Generic Name</label>
                <p className="text-sm text-gray-900">{lineItem.genericName || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
                <p className="text-sm text-gray-900">{lineItem.brandName || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Strength</label>
                <p className="text-sm text-gray-900">{lineItem.strength || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dosage Form</label>
                <p className="text-sm text-gray-900">{lineItem.dosageForm || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Line Item Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Line Item Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Line Number</label>
                <p className="text-sm text-gray-900">{lineItem.lineNo}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                <p className="text-sm text-gray-900">{lineItem.quantity}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                <p className="text-sm text-gray-900">${(parseFloat(lineItem.salesPrice) || 0).toFixed(2)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount</label>
                <p className="text-sm text-gray-900 font-semibold">${(parseFloat(lineItem.totalAmount) || 0).toFixed(2)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sales Date</label>
                <p className="text-sm text-gray-900">
                  {lineItem.salesDate ? new Date(lineItem.salesDate).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  lineItem.status === 'VALIDATED' ? 'bg-green-100 text-green-800' :
                  lineItem.status === 'IN_ERROR' ? 'bg-red-100 text-red-800' :
                  lineItem.status === 'POSTED' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {lineItem.status}
                </span>
              </div>
            </div>
          </div>

          {/* Claim Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Claim Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Claim Number</label>
                <p className="text-sm text-gray-900">{lineItem.claim?.claimNumber || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Claim Date</label>
                <p className="text-sm text-gray-900">
                  {lineItem.claim?.claimDate ? new Date(lineItem.claim.claimDate).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-900">{lineItem.claim?.customer?.name || 'N/A'}</p>
                  {lineItem.claim?.customer?.id && (
                    <button
                      onClick={() => navigate(`/customers/${lineItem.claim.customer.id}`)}
                      className="text-blue-600 hover:text-blue-800 underline text-xs"
                      title="View Customer"
                    >
                      View Customer
                    </button>
                  )}
                </div>
                {lineItem.claim?.customer?.ediEntityId && (
                  <p className="text-xs text-gray-500 mt-1">EDI ID: {lineItem.claim.customer.ediEntityId}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contract</label>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-900">{lineItem.claim?.contract?.number || 'N/A'}</p>
                  {lineItem.claim?.contract?.id && (
                    <button
                      onClick={() => navigate(`/contracts/${lineItem.claim.contract.id}`)}
                      className="text-blue-600 hover:text-blue-800 underline text-xs"
                      title="View Contract"
                    >
                      View Contract
                    </button>
                  )}
                </div>
                {lineItem.claim?.contract?.description && (
                  <p className="text-xs text-gray-500 mt-1">{lineItem.claim.contract.description}</p>
                )}
              </div>
            </div>
          </div>

          {/* Validation Errors */}
          {lineItem.validationErrors && lineItem.validationErrors.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
                Validation Errors
              </h2>
              <ul className="space-y-2">
                {lineItem.validationErrors.map((error, index) => (
                  <li key={index} className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-red-700">{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Validation Status */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Validation Status</h3>
            <div className="space-y-4">
              <div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${validationInfo.color}`}>
                  <ValidationIcon className="w-4 h-4 mr-2" />
                  {validationInfo.label}
                </span>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Price Valid:</span>
                  <span className={lineItem.isPriceValid ? 'text-green-600' : 'text-red-600'}>
                    {lineItem.isPriceValid ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Quantity Valid:</span>
                  <span className={lineItem.isQuantityValid ? 'text-green-600' : 'text-red-600'}>
                    {lineItem.isQuantityValid ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Product Valid:</span>
                  <span className={lineItem.isProductValid ? 'text-green-600' : 'text-red-600'}>
                    {lineItem.isProductValid ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Contract Valid:</span>
                  <span className={lineItem.isContractValid ? 'text-green-600' : 'text-red-600'}>
                    {lineItem.isContractValid ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Compliance Status */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  lineItem.complianceStatus === 'COMPLIANT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {lineItem.complianceStatus || 'N/A'}
                </span>
              </div>
              {lineItem.complianceNotes && (
                <div>
                  <span className="text-sm text-gray-600">Notes:</span>
                  <p className="text-sm text-gray-900 mt-1">{lineItem.complianceNotes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Contract Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contract Information</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Contract Number:</span>
                <span className="font-medium">{lineItem.claim?.contract?.number || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Contract Description:</span>
                <span className="font-medium">{lineItem.claim?.contract?.description || 'N/A'}</span>
              </div>
              {lineItem.claim?.contract?.id && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Contract Details:</span>
                  <button
                    onClick={() => navigate(`/contracts/${lineItem.claim.contract.id}`)}
                    className="text-blue-600 hover:text-blue-800 underline text-sm"
                    title="View Contract"
                  >
                    View Contract
                  </button>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Contract Price:</span>
                <span className="font-medium">${(parseFloat(lineItem.contractPrice) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Validated Price:</span>
                <span className="font-medium">${(parseFloat(lineItem.validatedPrice) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Variance:</span>
                <span className={`font-medium ${parseFloat(lineItem.variance) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {lineItem.variance ? `${parseFloat(lineItem.variance).toFixed(2)}%` : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tolerance:</span>
                <span className="font-medium">{lineItem.tolerance ? `${parseFloat(lineItem.tolerance) * 100}%` : 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Within Tolerance:</span>
                <span className={lineItem.isWithinTolerance ? 'text-green-600' : 'text-red-600'}>
                  {lineItem.isWithinTolerance ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          {/* Pricing Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Sales Price:</span>
                <span className="font-medium">${(parseFloat(lineItem.salesPrice) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">List Price:</span>
                <span className="font-medium">${(parseFloat(lineItem.listPrice) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Rebate Price:</span>
                <span className="font-medium">${(parseFloat(lineItem.rebatePrice) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Chargeback Price:</span>
                <span className="font-medium">${(parseFloat(lineItem.chargebackPrice) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Net Price:</span>
                <span className="font-medium">${(parseFloat(lineItem.netPrice) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Discount Amount:</span>
                <span className="font-medium">${(parseFloat(lineItem.discountAmount) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Rebate Amount:</span>
                <span className="font-medium">${(parseFloat(lineItem.rebateAmount) || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {lineItem.notes && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
              <p className="text-sm text-gray-700">{lineItem.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <LineItemEditModal
        isOpen={showEditModal}
        onClose={handleCloseModal}
        lineItem={lineItem}
        onSave={handleSave}
        isLoading={updateMutation.isPending}
      />
    </div>
  );
};

export default LineItemDetails;
